/**
 * Binary introspection.
 *
 * Espressif images carry enough metadata to catch the mistakes that produce a
 * "flash succeeded, board boot-loops" outcome -- which is the worst failure
 * mode because the tool reports success. Reading the 24-byte image header tells
 * us which chip the binary was compiled for; reading the partition table tells
 * us where the app and filesystem are supposed to live. Both are checked
 * against the connected device before anything is written.
 *
 * Layouts referenced:
 *   esp_image_header_t   -- components/esp_bootloader_format/include/esp_bootloader_desc.h
 *   esp_partition_info_t -- components/bootloader_support/include/esp_flash_partitions.h
 */

import { chipFromImageChipId, type ChipProfile } from "./chips";

export const IMAGE_MAGIC = 0xe9;
export const PARTITION_MAGIC = 0x50aa;
export const PARTITION_MD5_MAGIC = 0xebeb;
export const PARTITION_ENTRY_SIZE = 32;
export const PARTITION_TABLE_MAX_SIZE = 0xc00;

export type FlashModeName = "qio" | "qout" | "dio" | "dout" | "fast_read" | "slow_read";

const FLASH_MODES: Record<number, FlashModeName> = {
  0: "qio",
  1: "qout",
  2: "dio",
  3: "dout",
  4: "fast_read",
  5: "slow_read",
};

// Low nibble of byte 3. The encoding is chip-dependent for newer parts, but
// these values are correct for ESP32/S2/S3 and are only used as a display hint.
const FLASH_FREQUENCIES: Record<number, string> = {
  0x0: "40m",
  0x1: "26m",
  0x2: "20m",
  0xf: "80m",
};

// High nibble of byte 3.
const FLASH_SIZES: Record<number, string> = {
  0x0: "1MB",
  0x1: "2MB",
  0x2: "4MB",
  0x3: "8MB",
  0x4: "16MB",
  0x5: "32MB",
  0x6: "64MB",
  0x7: "128MB",
};

export interface EspImageHeader {
  segmentCount: number;
  flashMode: FlashModeName | null;
  flashFrequency: string | null;
  flashSize: string | null;
  entryAddress: number;
  /** Absent on ESP8266 images, which predate the chip-id field. */
  chipId: number | null;
  chip: ChipProfile | undefined;
  hashAppended: boolean;
}

export interface PartitionEntry {
  label: string;
  type: number;
  subType: number;
  offset: number;
  size: number;
  typeName: string;
  subTypeName: string;
}

const APP_SUBTYPES: Record<number, string> = {
  0x00: "factory",
  0x20: "test",
};

const DATA_SUBTYPES: Record<number, string> = {
  0x00: "ota",
  0x01: "phy",
  0x02: "nvs",
  0x03: "coredump",
  0x04: "nvs_keys",
  0x05: "efuse",
  0x06: "undefined",
  0x80: "esphttpd",
  0x81: "fat",
  0x82: "spiffs",
  0x83: "littlefs",
};

function describeSubType(type: number, subType: number): string {
  if (type === 0x00) {
    if (subType >= 0x10 && subType <= 0x1f) return `ota_${subType - 0x10}`;
    return APP_SUBTYPES[subType] ?? `0x${subType.toString(16)}`;
  }
  if (type === 0x01) {
    return DATA_SUBTYPES[subType] ?? `0x${subType.toString(16)}`;
  }
  return `0x${subType.toString(16)}`;
}

/**
 * Parse an application/bootloader image header.
 * Returns null when `bytes` does not start with a valid header at `start`.
 */
export function parseImageHeader(bytes: Uint8Array, start = 0): EspImageHeader | null {
  if (bytes.length < start + 24) return null;
  if (bytes[start] !== IMAGE_MAGIC) return null;

  const segmentCount = bytes[start + 1];
  // A real image has 1..16 segments; anything else means we matched 0xE9 by chance.
  if (segmentCount === 0 || segmentCount > 16) return null;

  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const spiMode = bytes[start + 2];
  const spiSpeedAndSize = bytes[start + 3];
  const entryAddress = view.getUint32(start + 4, true);
  const chipId = view.getUint16(start + 12, true);
  const hashAppended = bytes[start + 23] === 1;

  // 0xFFFF is what a chip-id-less (ESP8266-era) or unpopulated field looks like.
  const knownChipId = chipId !== 0xffff ? chipId : null;

  return {
    segmentCount,
    flashMode: FLASH_MODES[spiMode] ?? null,
    flashFrequency: FLASH_FREQUENCIES[spiSpeedAndSize & 0x0f] ?? null,
    flashSize: FLASH_SIZES[(spiSpeedAndSize >> 4) & 0x0f] ?? null,
    entryAddress,
    chipId: knownChipId,
    chip: knownChipId !== null ? chipFromImageChipId(knownChipId) : undefined,
    hashAppended,
  };
}

/**
 * Parse a partition table image (the blob that lives at 0x8000).
 * Returns null if the leading entry is not a partition record.
 */
export function parsePartitionTable(bytes: Uint8Array, start = 0): PartitionEntry[] | null {
  if (bytes.length < start + PARTITION_ENTRY_SIZE) return null;

  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  if (view.getUint16(start, true) !== PARTITION_MAGIC) return null;

  const entries: PartitionEntry[] = [];
  const limit = Math.min(bytes.length, start + PARTITION_TABLE_MAX_SIZE);

  for (let cursor = start; cursor + PARTITION_ENTRY_SIZE <= limit; cursor += PARTITION_ENTRY_SIZE) {
    const magic = view.getUint16(cursor, true);

    // The table is terminated by its own MD5 record or by erased flash.
    if (magic === PARTITION_MD5_MAGIC) continue;
    if (magic !== PARTITION_MAGIC) break;

    const type = bytes[cursor + 2];
    const subType = bytes[cursor + 3];
    const offset = view.getUint32(cursor + 4, true);
    const size = view.getUint32(cursor + 8, true);

    const labelBytes = bytes.subarray(cursor + 12, cursor + 28);
    const nulIndex = labelBytes.indexOf(0);
    const label = new TextDecoder()
      .decode(nulIndex === -1 ? labelBytes : labelBytes.subarray(0, nulIndex))
      .trim();

    entries.push({
      label,
      type,
      subType,
      offset,
      size,
      typeName: type === 0 ? "app" : type === 1 ? "data" : `0x${type.toString(16)}`,
      subTypeName: describeSubType(type, subType),
    });
  }

  return entries.length > 0 ? entries : null;
}

/**
 * True when the blob looks like `esptool merge_bin` output: a whole-flash image
 * that already contains the bootloader, the table at its absolute 0x8000, and
 * the app. Such a file must be written at 0x0, never at 0x10000.
 */
export function looksLikeMergedImage(bytes: Uint8Array): boolean {
  if (bytes.length <= 0x8000 + PARTITION_ENTRY_SIZE) return false;

  const hasTableAtAbsoluteOffset = parsePartitionTable(bytes, 0x8000) !== null;
  if (!hasTableAtAbsoluteOffset) return false;

  // The bootloader sits at 0x0 (S3/C3/C6/H2) or 0x1000 (ESP32/S2) inside the
  // merged image; P4 uses 0x2000.
  return (
    parseImageHeader(bytes, 0) !== null ||
    parseImageHeader(bytes, 0x1000) !== null ||
    parseImageHeader(bytes, 0x2000) !== null
  );
}

export function formatBytes(size: number): string {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
}

export function parseFlashSizeToBytes(label: string): number | null {
  const match = /^(\d+)\s*(KB|MB)$/i.exec(label.trim());
  if (!match) return null;
  const value = Number(match[1]);
  return match[2].toUpperCase() === "MB" ? value * 1024 * 1024 : value * 1024;
}
